import { Facebook, Instagram, Twitter, MapPin, Phone, Mail } from 'lucide-react';

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
    { icon: Instagram, href: 'https://instagram.com', label: 'Instagram' },
    { icon: Twitter, href: 'https://twitter.com', label: 'Twitter' },
  ];

  const quickLinks = [
    { name: 'Inicio', href: '#inicio' },
    { name: 'Menú', href: '#menu' },
    { name: 'Galería', href: '#galeria' },
    { name: 'Reseñas', href: '#resenas' },
    { name: 'Contacto', href: '#contacto' },
  ];

  const legalLinks = [
    { name: 'Aviso Legal', href: '/aviso-legal', testid: 'footer-legal-notice' },
    { name: 'Política de Privacidad', href: '/privacidad', testid: 'footer-privacy' },
    { name: 'Términos y Condiciones', href: '/terminos', testid: 'footer-terms' },
    { name: 'Política de Cookies', href: '/cookies', testid: 'footer-cookies' },
    { name: 'Política de Reembolsos', href: '/reembolsos', testid: 'footer-refund' },
    { name: 'Información de Alérgenos', href: '/alergenos', testid: 'footer-allergens' },
  ];

  const scrollToSection = (href) => {
    const element = document.querySelector(href);
    if (element) element.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <footer className="bg-[#2D231E] border-t border-[#FDFBF7]/10" data-testid="footer">
      <div className="container-max py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
          {/* Brand + Social */}
          <div>
            <div className="flex items-center gap-2 mb-4">
              <span className="font-serif text-2xl font-bold text-[#C44536]">Taj Mahal</span>
            </div>
            <p className="font-sans text-sm text-[#FDFBF7]/70 mb-4">
              Auténtica cocina india en San Fernando, Cádiz. Sabores tradicionales con un toque moderno.
            </p>
            <div className="flex gap-3">
              {socialLinks.map((s) => (
                <a
                  key={s.label}
                  href={s.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-[#FDFBF7]/10 flex items-center justify-center text-[#FDFBF7] hover:bg-[#C44536] transition-colors"
                  aria-label={s.label}
                  data-testid={`social-${s.label.toLowerCase()}`}
                >
                  <s.icon className="w-5 h-5" />
                </a>
              ))}
            </div>
          </div>

          {/* Quick links */}
          <div>
            <h3 className="font-sans font-semibold text-[#FDFBF7] mb-4">Enlaces Rápidos</h3>
            <ul className="space-y-2">
              {quickLinks.map((l) => (
                <li key={l.name}>
                  <a
                    href={l.href}
                    onClick={(e) => { e.preventDefault(); scrollToSection(l.href); }}
                    className="font-sans text-sm text-[#FDFBF7]/70 hover:text-[#E28743] transition-colors"
                  >
                    {l.name}
                  </a>
                </li>
              ))}
            </ul>
            <h3 className="font-sans font-semibold text-[#FDFBF7] mt-6 mb-3">Horario</h3>
            <ul className="space-y-1 font-sans text-sm text-[#FDFBF7]/70">
              <li className="flex justify-between"><span>Lun - Dom</span><span>12:00 - 16:00</span></li>
              <li className="flex justify-between"><span>Lun - Dom</span><span>19:00 - 23:30</span></li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-sans font-semibold text-[#FDFBF7] mb-4">Información Legal</h3>
            <ul className="space-y-2">
              {legalLinks.map((l) => (
                <li key={l.name}>
                  <a
                    href={l.href}
                    className="font-sans text-sm text-[#FDFBF7]/70 hover:text-[#E28743] transition-colors"
                    data-testid={l.testid}
                  >
                    {l.name}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Business registration */}
          <div>
            <h3 className="font-sans font-semibold text-[#FDFBF7] mb-4">Datos del Titular</h3>
            <ul className="space-y-2 font-sans text-sm text-[#FDFBF7]/70" data-testid="footer-business-info">
              <li><strong className="text-[#FDFBF7]/90">Talwinder Singh Kaur</strong></li>
              <li>NIF: <span className="text-[#FDFBF7]/90">78317925G</span></li>
              <li className="text-xs italic">Nombre comercial: Taj Mahal San Fernando</li>
              <li className="flex items-start gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Calle Real 109<br />11100 San Fernando, Cádiz<br />España</span>
              </li>
              <li className="flex items-center gap-2">
                <Phone className="w-4 h-4 shrink-0" />
                <a href="tel:+34602704745" className="hover:text-[#E28743] transition-colors">+34 602 704 745</a>
              </li>
              <li className="flex items-center gap-2">
                <Mail className="w-4 h-4 shrink-0" />
                <a href="mailto:Tajmahalsafernando@gmail.com" className="hover:text-[#E28743] transition-colors break-all">
                  Tajmahalsafernando@gmail.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright */}
        <div className="pt-8 border-t border-[#FDFBF7]/10 text-center space-y-1">
          <p className="font-sans text-sm text-[#FDFBF7]/50">
            © {currentYear} Talwinder Singh Kaur (Taj Mahal San Fernando) · NIF 78317925G · Todos los derechos reservados.
          </p>
          <p className="font-sans text-xs text-[#FDFBF7]/40">
            Establecimiento inscrito en el Registro Sanitario de Andalucía. Precios con IVA incluido.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
